/**
 * NQG-RQ-29 — Portfolio API client.
 */

import axios from '../utils/axios';

const API_URL = '/portfolios';

export interface Portfolio {
  _id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  pdfCount: number;
  createdBy: string;
  createdAt: string;
}

// NQG-RQ-31 — hierarchical taxonomy (Theme → Subtheme → Sub-subtheme)
export interface TaxonomyNode {
  _id: string;
  portfolioId: string;
  name: string;
  parentId: string | null;
  level: 1 | 2 | 3;
}

export interface PdfTaxonomyPlacement {
  portfolioId: string;
  nodeId: string;
  pathIds: string[];
  pathNames: string[];
}

export const TAXONOMY_LEVEL_LABELS: Record<number, string> = {
  1: 'Theme',
  2: 'Subtheme',
  3: 'Sub-subtheme',
};

export const TAXONOMY_PATH_SEPARATOR = ' › ';

class PortfolioService {
  private authHeaders() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  async list(): Promise<Portfolio[]> {
    const res = await axios.get(API_URL, { headers: this.authHeaders() });
    return res.data.data;
  }

  async create(name: string, description?: string): Promise<Portfolio> {
    const res = await axios.post(API_URL, { name, description }, { headers: this.authHeaders() });
    return res.data.data;
  }

  async rename(id: string, name: string): Promise<Portfolio> {
    const res = await axios.patch(`${API_URL}/${id}`, { name }, { headers: this.authHeaders() });
    return res.data.data;
  }

  async merge(id: string, targetId: string): Promise<{ merged: boolean; into: string }> {
    const res = await axios.post(`${API_URL}/${id}/merge`, { targetId }, { headers: this.authHeaders() });
    return res.data.data;
  }

  async remove(id: string): Promise<{ deleted: boolean; migratedTo: string }> {
    const res = await axios.delete(`${API_URL}/${id}`, { headers: this.authHeaders() });
    return res.data.data;
  }

  async setPdfPortfolios(pdfId: string, portfolios: string[]): Promise<{ pdfId: string; portfolios: string[] }> {
    const res = await axios.put(`${API_URL}/pdf/${pdfId}`, { portfolios }, { headers: this.authHeaders() });
    return res.data.data;
  }

  // ── Portfolio detail (its PDFs) + bulk assign ──

  async pdfsInPortfolio(id: string): Promise<{ portfolio: Portfolio; pdfs: any[] }> {
    const res = await axios.get(`${API_URL}/${id}/pdfs`, { headers: this.authHeaders() });
    return res.data.data;
  }

  async addPdfsToPortfolio(id: string, pdfIds: string[]): Promise<{ added: number }> {
    const res = await axios.post(`${API_URL}/${id}/pdfs`, { pdfIds }, { headers: this.authHeaders() });
    return res.data.data;
  }

  async removePdfFromPortfolio(id: string, pdfId: string): Promise<{ removed: boolean }> {
    const res = await axios.delete(`${API_URL}/${id}/pdfs/${pdfId}`, { headers: this.authHeaders() });
    return res.data.data;
  }

  // ── NQG-RQ-31: hierarchical taxonomy ──

  async getTaxonomy(portfolioId: string): Promise<TaxonomyNode[]> {
    const res = await axios.get(`${API_URL}/${portfolioId}/taxonomy`, { headers: this.authHeaders() });
    return res.data.data;
  }

  async createNode(portfolioId: string, name: string, parentId?: string | null): Promise<TaxonomyNode> {
    const res = await axios.post(
      `${API_URL}/${portfolioId}/taxonomy`,
      { name, parentId: parentId || null },
      { headers: this.authHeaders() }
    );
    return res.data.data;
  }

  async renameNode(nodeId: string, name: string): Promise<TaxonomyNode> {
    const res = await axios.patch(`${API_URL}/taxonomy/node/${nodeId}`, { name }, { headers: this.authHeaders() });
    return res.data.data;
  }

  async moveNode(nodeId: string, parentId: string | null): Promise<TaxonomyNode> {
    const res = await axios.post(`${API_URL}/taxonomy/node/${nodeId}/move`, { parentId }, { headers: this.authHeaders() });
    return res.data.data;
  }

  async deleteNode(nodeId: string): Promise<{ deleted: number; pdfsUnplaced: number }> {
    const res = await axios.delete(`${API_URL}/taxonomy/node/${nodeId}`, { headers: this.authHeaders() });
    return res.data.data;
  }

  async setPdfTaxonomy(pdfId: string, nodeIds: string[]): Promise<{ pdfId: string; placements: PdfTaxonomyPlacement[] }> {
    const res = await axios.put(`${API_URL}/taxonomy/pdf/${pdfId}`, { nodeIds }, { headers: this.authHeaders() });
    return res.data.data;
  }
}

export const portfolioService = new PortfolioService();
